// supervisor-proposal-application.ts
//
// Wires proposal-applier.ts (P6 Agent A, previously never called from
// production — code-reviewer 260909-p6-m2 H1) into the control loop:
//   1. Maps `AutopilotResult.proposed_changes` (worker-reported shape:
//      op/target/expected_revision/replacement_ids/content) to
//      proposal-applier.ts's `ProposedChange` shape (op/ticket_id/patch/
//      new_ticket) — the two shapes exist for different audiences (§5.3
//      wire contract vs. in-memory transaction input) and were never meant
//      to be the same type.
//   2. Applies every proposed change from ONE attempt as ONE transaction
//      via `applyProposal` (stale `expected_revision` on ANY change rejects
//      the WHOLE batch — no partial apply, so a worker never sees half its
//      own proposal land).
//   3. Persists new/changed ticket JSON files to `.avatar/tickets/` (the
//      git-versioned source supervisor-ticket-projection.ts re-reads every
//      tick) and journals operation_intent/operation_result per change so a
//      crash mid-apply is reconcilable rather than silently re-applied
//      twice on restart (same operation_id -> already in store.state.results
//      -> skip, mirrors commit-owner.ts's intent-before-effect pattern).
//   4. (06e R3) adds the synthetic `supersede` of a blocked plan when the
//      source ticket is a controller redirect decision that created a slice
//      (redirect-decision-blocked-plan-supersede.ts); (06e R5) renders
//      `.avatar/PLAN.md` once the batch touched tickets.

import { randomUUID } from "node:crypto";
import type { AutopilotResult } from "./autopilot-result-schema.js";
import { writePlanIndex } from "./controller-plan-index-markdown.js";
import type { MissionStore } from "./mission-store.js";
import { applyProposal } from "./proposal-applier.js";
import { governanceRefusalInBatch } from "./proposal-batch-governance-prescan.js";
import {
  batchTargetCollisionReason,
  type CreatedInBatch,
  remapBatchDependencies,
} from "./proposal-batch-target-remap.js";
import { type ProposalRejectionCode, quoteWorkerValue } from "./proposal-change-types.js";
import { toApplierChange } from "./proposed-change-wire-mapping.js";
import { blockedPlanSupersedeChanges } from "./redirect-decision-blocked-plan-supersede.js";
import {
  type PendingProposalWrite,
  persistProposalBatch,
} from "./supervisor-proposal-batch-persistence.js";
import { loadTicketsFromRepo } from "./supervisor-ticket-projection.js";
import { checkTicketDagForBatch } from "./ticket-file-persistence.js";

export interface ApplyProposedChangesInput {
  store: MissionStore;
  repoDir: string;
  runId: string;
  ticketStatus: Record<string, string>;
  proposedChanges: AutopilotResult["proposed_changes"];
  /** R-25 (D2): dòng cảnh báo chính sách DAG (luật 3-4) — không chặn. */
  notify?: (line: string) => void;
  /** 06e R3: the ticket whose done attempt this is — may supersede a blocked plan. */
  sourceTicketId?: string;
}

export type ApplyProposedChangesResult =
  | { ok: true; touchedTicketIds: string[] }
  // F-72: `code` only for a worker-fixable refusal; synthetic supersede refusals stay uncoded.
  | { ok: false; reason: string; code?: ProposalRejectionCode };

/**
 * Applies every `proposed_changes` entry from one attempt as a single
 * transaction, persists the result, and journals a receipt per change.
 * Idempotent per `(runId, index)`: a change already recorded in
 * `store.state.results` (crash-then-restart replay) is skipped rather than
 * re-applied.
 */
export async function applyProposedChangesForAttempt(
  input: ApplyProposedChangesInput,
): Promise<ApplyProposedChangesResult> {
  if (input.proposedChanges.length === 0) return { ok: true, touchedTicketIds: [] };

  const tickets = await loadTicketsFromRepo(input.repoDir);
  // Audit F-1: governance over the WHOLE batch first — an uncoded refusal must
  // win over any fixable one, wherever it sits in the batch.
  const governance = governanceRefusalInBatch(tickets, input.proposedChanges);
  if (governance !== undefined) return { ok: false, reason: governance };
  const collision = batchTargetCollisionReason(tickets, input.proposedChanges);
  if (collision !== undefined) return { ok: false, reason: collision, code: "batch_collision" };
  let currentTickets = tickets;
  // H6 target→id pairs of THIS pass. A crash-then-replay skips the already
  // applied creates, so their pairs are absent and a dependency on them fails
  // the DAG check below — closed, never a wrong edge (known narrow gap).
  const created: CreatedInBatch[] = [];
  let currentStatus = { ...input.ticketStatus };
  const touched = new Set<string>();
  const pendingWrites: PendingProposalWrite[] = [];

  for (let index = 0; index < input.proposedChanges.length; index++) {
    const raw = input.proposedChanges[index];
    if (raw === undefined) continue;
    const operationId = `apply-proposal-${input.runId}-${index}`;
    if (input.store.state.results[operationId] !== undefined) continue; // already applied

    const mapped = toApplierChange(raw, currentTickets);
    if (!mapped.ok) {
      return {
        ok: false,
        reason: `proposed_changes[${index}] (${raw.op} ${quoteWorkerValue(raw.target)}): ${mapped.reason}`,
        ...(mapped.code === undefined ? {} : { code: mapped.code }),
      };
    }
    const applierChange = mapped.change;
    const result = applyProposal(currentTickets, currentStatus, applierChange);
    if (!result.ok) {
      return {
        ok: false,
        reason: `proposed_changes[${index}] (${raw.op} ${quoteWorkerValue(raw.target)}): ${result.reason}`,
        ...(result.code === undefined ? {} : { code: result.code }),
      };
    }
    currentTickets = result.tickets;
    currentStatus = result.statusMap;
    const touchedId =
      applierChange.op === "create" ? applierChange.new_ticket?.id : applierChange.ticket_id;
    if (touchedId !== undefined) touched.add(touchedId);
    if (applierChange.op === "create" && touchedId !== undefined) {
      created.push({ target: raw.target, id: touchedId });
    }
    if (raw.op === "cancel") {
      for (const dep of currentTickets) {
        if (dep.depends_on.includes(raw.target)) touched.add(dep.id);
      }
    }
    pendingWrites.push({ operationId, index, change: applierChange });
  }

  // H6: dependencies on a batch `target` now name the id it was assigned —
  // BEFORE the DAG check, which would otherwise see an unknown id.
  currentTickets = remapBatchDependencies(currentTickets, created);

  // 06e R3: judged on the remapped tickets + the creates of THIS pass (B7).
  const { supersedes, dependentNotices } = blockedPlanSupersedeChanges({
    sourceTicket: currentTickets.find((t) => t.id === input.sourceTicketId),
    state: input.store.state,
    runId: input.runId,
    tickets: currentTickets,
    created,
    ticketStatus: input.ticketStatus,
    currentStatus,
    proposedChanges: input.proposedChanges,
  });
  for (const [k, item] of supersedes.entries()) {
    const result = applyProposal(currentTickets, currentStatus, item.change);
    if (!result.ok) return { ok: false, reason: `${item.operationId}: ${result.reason}` };
    currentStatus = result.statusMap;
    const index = input.proposedChanges.length + k;
    const synthetic = { planId: item.planId, notice: item.notice };
    pendingWrites.push({ operationId: item.operationId, index, change: item.change, synthetic });
  }

  // R-25 (D2): kiểm DAG của LÔ SAU KHI áp toàn bộ trong bộ nhớ, TRƯỚC khi
  // ghi bất kỳ file nào — dep không tồn tại/vòng chặn cả lô (0 file ghi);
  // thiếu testcase/verification recipe chỉ notify, lô vẫn áp.
  const dagCheck = checkTicketDagForBatch(currentTickets, input.notify);
  if (!dagCheck.ok) return { ok: false, reason: dagCheck.reason, code: "dag" };

  await persistProposalBatch({
    store: input.store,
    repoDir: input.repoDir,
    runId: input.runId,
    tickets: currentTickets,
    pendingWrites,
    notify: input.notify,
  });
  // 06f R6 (L2): only once the batch is persisted — a DAG-refused batch says nothing.
  for (const line of dependentNotices) input.notify?.(line);

  const events = [...touched].map((ticketId) => ({
    event_id: randomUUID(),
    delta: {
      kind: "ticket_state_changed" as const,
      ticket: ticketId,
      status: currentStatus[ticketId] ?? "todo",
    },
  }));
  if (events.length > 0) await input.store.commit(events);
  // 06e R5: the DAG index every `plan_path` names, committed by the caller.
  if (touched.size > 0) await writePlanIndex(input.repoDir, currentTickets, currentStatus);

  return { ok: true, touchedTicketIds: [...touched] };
}
